"use client"

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import apiClient from '../lib/api-client'
import { API_BASE_URL } from '../config'
import { X, Eye, Edit, Save } from 'lucide-react'

// Import UI components individually
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog'
import { Skeleton } from './ui/skeleton'
import { Badge } from './ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { sortPlayersByJerseyAndName } from '../utils/playerSorting';

export default function LibraryTab({ selectedMatchId, setSelectedMatchId, selectedSponsorId, setSelectedSponsorId, selectedPlayerId, setSelectedPlayerId }) {
  const [images, setImages] = useState([])
  const [matches, setMatches] = useState([])
  const [matchesWithImages, setMatchesWithImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false)
  const [selectedMatch, setSelectedMatch] = useState('')
  const [selectedSponsors, setSelectedSponsors] = useState([])
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [sortBy] = useState('date')
  const [sponsors, setSponsors] = useState([])
  const [players, setPlayers] = useState([])
  const [viewMode, setViewMode] = useState('matches') // 'matches' or 'images'
  const [pageSize] = useState(25)
  const [currentPage, setCurrentPage] = useState(1)
  const [displayedImages, setDisplayedImages] = useState([])
  


  // State for sponsor tagging
  const [selectedImage] = useState(null);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [availableSponsors, setAvailableSponsors] = useState([]);
  const [selectedSponsorsForTagging, setSelectedSponsorsForTagging] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(false);

  // State for batch processing
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelectedImages, setBatchSelectedImages] = useState([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchReassignDialog, setBatchReassignDialog] = useState(false);
  const [batchSelectedMatch, setBatchSelectedMatch] = useState('');
  const [batchRemoveSponsorsDialog, setBatchRemoveSponsorsDialog] = useState(false);
  const [batchSponsorsToRemove, setBatchSponsorsToRemove] = useState([]);

  // State for batch re-analysis
  const [batchReanalysisDialog, setBatchReanalysisDialog] = useState(false);
  const [reanalysisProcessing, setReanalysisProcessing] = useState(false);
  const [reanalysisProgress, setReanalysisProgress] = useState(0);
  const [reanalysisTotal, setReanalysisTotal] = useState(0);

  // State for image detail modal
  const [selectedImageForDetail, setSelectedImageForDetail] = useState(null);
  const [isImageDetailModalOpen, setIsImageDetailModalOpen] = useState(false);
  const [isEditingImageDetails, setIsEditingImageDetails] = useState(false);
  const [showAiResponse, setShowAiResponse] = useState(false);
  const [editingImageData, setEditingImageData] = useState({});
  const [isEditingSponsors, setIsEditingSponsors] = useState(false);
  const [editingSponsors, setEditingSponsors] = useState([]);
  const [isEditingPlayers, setIsEditingPlayers] = useState(false);
  const [editingPlayers, setEditingPlayers] = useState([]);

  // Memoized sorted players to prevent sorting on every render
  const sortedPlayers = useMemo(() => {
    return sortPlayersByJerseyAndName(players);
  }, [players]);

  // Memoized sorted editing players to prevent sorting on every render
  const sortedEditingPlayers = useMemo(() => {
    return sortPlayersByJerseyAndName(editingPlayers);
  }, [editingPlayers]);

  // Helper function to get match title from match ID
  const getMatchTitle = useCallback((matchId) => {
    if (!matchId) return 'Unknown Match';
    const match = matches.find(m => m.id === matchId);
    return match ? match.title : 'Unknown Match';
  }, [matches]);
  
  
  // Fetch sponsors for tagging
  const fetchSponsorsForTagging = async () => {
    setLoadingSponsors(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/sponsors`);
      
      let sponsorsData = [];
      if (Array.isArray(response.data)) {
        sponsorsData = response.data;
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        sponsorsData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        sponsorsData = [response.data];
      }
      
      setAvailableSponsors(sponsorsData);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
    } finally {
      setLoadingSponsors(false);
    }
  }
  
  
  // Toggle sponsor selection
  const toggleSponsor = (sponsor) => {
    const isSelected = selectedSponsorsForTagging.some(s => s.id === sponsor.id);
    
    if (isSelected) {
      // Remove sponsor
      setSelectedSponsorsForTagging(selectedSponsorsForTagging.filter(s => s.id !== sponsor.id));
    } else {
      // Add sponsor with default values
      setSelectedSponsorsForTagging([...selectedSponsorsForTagging, {
        id: sponsor.id,
        name: sponsor.name,
        confidence: 0.8,
        visibility: 0.7
      }]);
    }
  };
  

  
  // --- DATA FETCHING & HELPERS (ORDERED) ---

  const constructS3Url = useCallback((key) => {
    if (!key) return null;
    const cleanKey = key.startsWith('/') ? key.substring(1) : key;
    return `https://sponsor-detection-images.s3.eu-west-1.amazonaws.com/${encodeURIComponent(cleanKey)}`;
  }, []);

  const getImageUrl = useCallback((image) => {
    if (!image) {
      return null;
    }
    
    if (image.url) {
      return image.url;
    }
    if (image.imageUrl) {
      return image.imageUrl;
    }
    if (image.s3Url) {
      return image.s3Url;
    }
    if (image.s3Key) {
      return constructS3Url(image.s3Key);
    }
    if (image.metadata && image.metadata.s3Key) {
      return constructS3Url(image.metadata.s3Key);
    }
    if (image.id && (image.id.includes('/') || image.id.startsWith('batch/'))) {
      return constructS3Url(image.id);
    }
    return null;
  }, [constructS3Url]);

  // Function to get fresh URL when image fails to load
  const getFreshImageUrl = useCallback(async (imageId) => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/images/${imageId}/url`);
      
      // Check if response has the success flag and data
      if (response.success && response.data && response.data.url) {
        return response.data.url;
      } 
      // Handle direct data structure (no success wrapper)
      else if (response.data && response.data.url) {
        return response.data.url;
      } 
      // Handle nested data structure
      else if (response.data && response.data.data && response.data.data.url) {
        return response.data.data.url;
      }
    } catch (error) {
      console.error('Failed to get fresh image URL:', error);
    }
    return null;
  }, []);

  const processAndSetImages = useCallback((data) => {
    let processedImages = [];
    if (Array.isArray(data)) {
      processedImages = data;
    } else if (data?.images && Array.isArray(data.images)) {
      processedImages = data.images;
    } else if (data?.data && Array.isArray(data.data)) {
      processedImages = data.data;
    } else if (data && typeof data === 'object' && !Array.isArray(data)) {
      processedImages = [data];
    }

    processedImages = processedImages.map(image => ({
      ...image,
      id: image.id || image._id || `temp-${Date.now()}`,
      url: getImageUrl(image),
      _metadataFetched: false
    }));

    if (selectedMatch) {
      processedImages = processedImages.filter(img => 
        img.matchId === selectedMatch || (img.metadata && img.metadata.matchId === selectedMatch)
      );
    }
    setImages(processedImages);
  }, [selectedMatch, getImageUrl]);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      
      // Build query parameters - use selectedMatchId prop if available, otherwise selectedMatch state
      const matchId = selectedMatchId || selectedMatch;
      if (matchId) params.matchId = matchId;
      if (showUnassignedOnly) params.unassignedOnly = true;
      if (selectedSponsors.length > 0) params.sponsors = selectedSponsors.join(',');
      if (selectedPlayers.length > 0) params.players = selectedPlayers.join(',');
      // Default to sorting by date (newest first) if no sortBy specified
      params.sortBy = sortBy || 'date';
      
      const response = await apiClient.get(`${API_BASE_URL}/images`, { params });
      
      // Handle different response formats from the backend
      if (response.data) {
        if (response.data.items && Array.isArray(response.data.items)) {
          // Handle the format: {"items": [...]}
          processAndSetImages(response.data.items);
        } else if (response.data.success && Array.isArray(response.data.data)) {
          // Handle the format: {"success": true, "data": [...]}
          processAndSetImages(response.data.data);
        } else if (Array.isArray(response.data)) {
          // Handle direct array format
          processAndSetImages(response.data);
        } else {
          console.warn('Unexpected response format:', response.data);
          setImages([]);
        }
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMatchId, selectedMatch, showUnassignedOnly, selectedSponsors, selectedPlayers, sortBy, processAndSetImages]);

  const fetchMatches = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/matches`);
      let matchesData = [];
      if (Array.isArray(response.data)) {
        matchesData = response.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        matchesData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        matchesData = [response.data];
      }
      setMatches(matchesData);
    } catch (error) {
      console.error('Error fetching matches, trying localStorage:', error);
      try {
        const storedMatches = localStorage.getItem('matches');
        if (storedMatches) setMatches(JSON.parse(storedMatches));
      } catch (parseError) { /* ignore */ }
    }
  }, []);

  const fetchSponsors = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/sponsors`);
      let sponsorsData = [];
      if (Array.isArray(response.data)) {
        sponsorsData = response.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        sponsorsData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        sponsorsData = [response.data];
      }
      setSponsors(sponsorsData.map(s => ({ ...s, id: s.id || s._id, name: s.name || 'Unnamed' })));
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      setSponsors([]);
    }
  }, []);

  const fetchPlayers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/players`);
      let playersData = [];
      if (Array.isArray(response.data)) {
        playersData = response.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        playersData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        playersData = [response.data];
      }
      setPlayers(playersData.map(p => ({ ...p, id: p.id || p._id, name: p.name || 'Unnamed' })));
    } catch (error) {
      console.error('Error fetching players:', error);
      setPlayers([]);
    }
  }, []);

  const fetchMatchesWithImages = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/matches-with-images`);
      
      // Process the matches data from the API
      const processedMatches = response.data.map(match => {
        // Get images from the match data
        const images = match.images?.items || [];
        const imageCount = images.length;
        
        // Get the first image URL if available
        let imageUrl = null;
        if (images.length > 0) {
          const firstImage = images[0];
          imageUrl = getImageUrl(firstImage);
        }
        
        return {
          ...match,
          imageCount,
          imageUrl
        };
      }).filter(match => match.imageCount > 0); // Only show matches with images
      
      setMatchesWithImages(processedMatches);
    } catch (error) {
      console.error('Error fetching matches-with-images, using fallback:', error);
      try {
        const imagesResponse = await apiClient.get(`${API_BASE_URL}/images`);
        let allImages = [];
        if (Array.isArray(imagesResponse.data)) allImages = imagesResponse.data;
        else if (imagesResponse.data?.images) allImages = imagesResponse.data.images;

        const matchesWithImagesData = matches
          .map(match => {
            const matchImages = allImages.filter(img => img.matchId === match.id);
            return {
              ...match,
              imageCount: matchImages.length,
              imageUrl: matchImages.length > 0 ? getImageUrl(matchImages[0]) : null
            };
          })
          .filter(match => match.imageCount > 0);
        setMatchesWithImages(matchesWithImagesData);
      } catch (fallbackError) {
        console.error('Error in fetchMatchesWithImages fallback:', fallbackError);
        setMatchesWithImages(matches.map(m => ({ ...m, imageCount: 0, imageUrl: null })));
      }
    }
  }, [matches, getImageUrl]);

  const saveSponsorsForImage = useCallback(async () => {
    if (!selectedImage) return;
    try {
      for (const sponsor of selectedSponsorsForTagging) {
        await apiClient.post(`${API_BASE_URL}/images/${selectedImage.id}/sponsor/${sponsor.id}`, {
          confidence: sponsor.confidence,
          visibility: sponsor.visibility
        });
      }
      fetchImages();
      setShowTagDialog(false);
    } catch (error) {
      console.error('Error saving sponsor tags:', error);
    }
  }, [selectedImage, selectedSponsorsForTagging, fetchImages]);

  // --- EFFECTS ---

  useEffect(() => {
    if (selectedMatchId) {
      setSelectedMatch(selectedMatchId);
      setViewMode('images');
    }
  }, [selectedMatchId]);

  useEffect(() => {
    if (selectedSponsorId) {
      // Set sponsor filter and switch to images view
      setSelectedSponsors([selectedSponsorId]);
      setViewMode('images');
    }
  }, [selectedSponsorId]);

  useEffect(() => {
    if (selectedPlayerId) {
      // Set player filter and switch to images view
      setSelectedPlayers([selectedPlayerId]);
      setViewMode('images');
    }
  }, [selectedPlayerId]);

  // If opened directly without match, sponsor, or player selection, show all images
  useEffect(() => {
    if (!selectedMatchId && !selectedSponsorId && !selectedPlayerId) {
      setViewMode('images');
    }
  }, [selectedMatchId, selectedSponsorId, selectedPlayerId]);

  useEffect(() => {
    if (setSelectedMatchId && selectedMatch) {
      setSelectedMatchId(selectedMatch);
    }
  }, [selectedMatch, setSelectedMatchId]);

  useEffect(() => {
    fetchMatches();
    fetchSponsors();
    fetchPlayers();
  }, [fetchMatches, fetchSponsors, fetchPlayers]);

  useEffect(() => {
    if (matches.length > 0) {
      fetchMatchesWithImages();
    }
  }, [matches, fetchMatchesWithImages]);

  useEffect(() => {
    if (viewMode === 'images') {
      fetchImages();
    }
  }, [viewMode, fetchImages]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setDisplayedImages(images.slice(startIndex, endIndex));
  }, [images, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMatch, selectedSponsors, sortBy]);
  

  
  // --- Image Detail Modal Handlers ---
  const handleOpenImageDetailModal = (image) => {
    setSelectedImageForDetail(image);
    setIsImageDetailModalOpen(true);
    setIsEditingImageDetails(false);
    setEditingImageData({
      filename: image.metadata?.filename || image.originalFilename || image.filename || '',
      matchId: image.matchId || '',
      uploader: image.metadata?.uploader || image.uploader || 'System',
      metadata: image.metadata || {}
    });
  };

  const handleCloseImageDetailModal = () => {
    setIsImageDetailModalOpen(false);
    setSelectedImageForDetail(null);
    setIsEditingImageDetails(false);
    setEditingImageData({});
    setIsEditingSponsors(false);
    setShowAiResponse(false);
    setEditingSponsors([]);
    setIsEditingPlayers(false);
    setEditingPlayers([]);
  };

  const handleStartEditingImageDetails = () => {
    setIsEditingImageDetails(true);
  };

  const handleCancelEditingImageDetails = () => {
    setIsEditingImageDetails(false);
    setEditingImageData({
      filename: selectedImageForDetail.metadata?.filename || selectedImageForDetail.originalFilename || selectedImageForDetail.filename || '',
      matchId: selectedImageForDetail.matchId || '',
      metadata: selectedImageForDetail.metadata || {}
    });
  };

  const handleSaveImageDetails = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.put(`/images/${selectedImageForDetail.id}`, {
        filename: editingImageData.filename,
        matchId: editingImageData.matchId,
        metadata: {
          ...editingImageData.metadata,
          filename: editingImageData.filename,
          uploader: editingImageData.uploader
        }
      });

      if (response.success) {
        // Update the selected image with new data
        const updatedImage = { ...selectedImageForDetail, ...response.data };
        setSelectedImageForDetail(updatedImage);
        
        // Refresh the images list
        fetchImages();
        
        setIsEditingImageDetails(false);
        alert('Image details updated successfully');
      }
    } catch (error) {
      console.error('Error updating image details:', error);
      alert('Failed to update image details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSponsors = () => {
    setIsEditingSponsors(true);
    setEditingSponsors([...(selectedImageForDetail.sponsors || [])]);
  };

  const handleCancelEditSponsors = () => {
    setIsEditingSponsors(false);
    setEditingSponsors([]);
  };

  const handleSaveSponsors = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.put(`/images/${selectedImageForDetail.id}`, {
        sponsors: editingSponsors
      });

      if (response.success) {
        // Update the selected image with new sponsor data
        const updatedImage = { ...selectedImageForDetail, sponsors: editingSponsors };
        setSelectedImageForDetail(updatedImage);
        
        // Refresh the images list
        fetchImages();
        
        setIsEditingSponsors(false);
        alert('Sponsors updated successfully');
      }
    } catch (error) {
      console.error('Error updating sponsors:', error);
      alert('Failed to update sponsors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSponsor = (sponsorId) => {
    if (!editingSponsors.includes(sponsorId)) {
      setEditingSponsors(prev => [...prev, sponsorId]);
    }
  };

  const handleRemoveSponsor = (sponsorId) => {
    setEditingSponsors(prev => prev.filter(id => id !== sponsorId));
  };

  const handleEditPlayers = () => {
    setIsEditingPlayers(true);
    setEditingPlayers([...(selectedImageForDetail.players || [])]);
  };

  const handleCancelEditPlayers = () => {
    setIsEditingPlayers(false);
    setEditingPlayers([]);
  };

  const handleSavePlayers = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.put(`/images/${selectedImageForDetail.id}`, {
        players: editingPlayers
      });

      if (response.success) {
        // Update the selected image with new player data
        const updatedImage = { ...selectedImageForDetail, players: editingPlayers };
        setSelectedImageForDetail(updatedImage);
        
        // Refresh the images list
        fetchImages();
        
        setIsEditingPlayers(false);
        alert('Players updated successfully');
      }
    } catch (error) {
      console.error('Error updating players:', error);
      alert('Failed to update players. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = (playerId) => {
    // Find the player object by ID
    const player = players.find(p => p.id === playerId);
    if (!player) return;
    
    const existingPlayer = editingPlayers.find(p => p.name === player.name);
    if (!existingPlayer) {
      setEditingPlayers(prev => [...prev, {
        name: player.name,
        jerseyNumber: player.jerseyNumber,
        position: player.position,
        team: player.team,
        action: 'Manually added',
        details: ''
      }]);
    }
  };

  const handleRemovePlayer = (index) => {
    setEditingPlayers(prev => prev.filter((_, i) => i !== index));
  };

  const handleSingleImageReanalysis = async (image) => {
    try {
      setLoading(true);
      
      const response = await apiClient.post('/batch', {
        images: [{
          id: image.id,
          s3Key: image.s3Key,
          filename: image.metadata?.filename || image.originalFilename || image.filename || 'unknown',
          matchId: image.matchId // Preserve the match association
        }],
        options: {
          reanalysis: true,
          preserveMatchId: true
        }
      });

      if (response.success) {
        alert('Image queued for re-analysis. Please refresh in a few minutes to see updated results.');
        // Refresh the images list
        fetchImages();
      } else {
        alert('Failed to queue image for re-analysis. Please try again.');
      }
    } catch (error) {
      console.error('Error re-analyzing single image:', error);
      alert('Failed to queue image for re-analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle sponsor filter changes
  const handleSponsorChange = (sponsorId) => {
    setSelectedSponsors(prev => {
      if (prev.includes(sponsorId)) {
        return prev.filter(id => id !== sponsorId)
      } else {
        return [...prev, sponsorId]
      }
    })
  }

  // Handle player filter changes
  const handlePlayerChange = (playerId) => {
    setSelectedPlayers(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId)
      } else {
        return [...prev, playerId]
      }
    })
  }
  
  
  
  
  
  // Handle match selection
  const handleMatchClick = (matchId) => {
    setSelectedMatch(matchId)
    setViewMode('images')
  }
  
  // Go back to matches view
  const handleBackToMatches = () => {
    setSelectedMatch('')
    setViewMode('matches')
  }
  
  // Toggle batch mode
  const toggleBatchMode = () => {
    setBatchMode(!batchMode);
    if (batchMode) {
      // Exiting batch mode, clear selections
      setBatchSelectedImages([]);
    }
  };

  // Toggle image selection for batch processing
  const toggleImageSelection = (imageId) => {
    setBatchSelectedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };



  // Open batch remove sponsors dialog
  const openBatchRemoveSponsorsDialog = () => {
    if (batchSelectedImages.length === 0) {
      alert('No Images Selected');
      return;
    }
    
    setBatchRemoveSponsorsDialog(true);
    fetchSponsorsForTagging(); // Reuse the existing function to fetch sponsors
  };

  // Process batch sponsor removal
  const processBatchRemoveSponsors = async () => {
    if (batchSponsorsToRemove.length === 0) {
      alert('No Sponsors Selected for Removal');
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    setBatchTotal(batchSelectedImages.length);
    
    try {
      // Get the selected images
      const selectedImages = images.filter(img => batchSelectedImages.includes(img.id));
      
      // Process each image
      for (let i = 0; i < selectedImages.length; i++) {
        const image = selectedImages[i];
        
        // Remove each selected sponsor from the image
        for (const sponsorId of batchSponsorsToRemove) {
          try {
            await apiClient.delete(`${API_BASE_URL}/images/${image.id}/sponsors/${sponsorId}`);
          } catch (error) {
            console.error(`Error removing sponsor ${sponsorId} from image ${image.id}:`, error);
            // Continue with other sponsors even if one fails
          }
        }
        
        setBatchProgress(i + 1);
      }
      
      // Refresh the images to reflect the changes
      fetchImages();
      
      alert(`Batch Sponsor Removal Complete. Successfully removed ${batchSponsorsToRemove.length} sponsors from ${batchSelectedImages.length} images.`);
    } catch (error) {
      console.error('Error during batch sponsor removal:', error);
      alert('Error during batch sponsor removal');
    } finally {
      setBatchProcessing(false);
      setBatchRemoveSponsorsDialog(false);
      setBatchSelectedImages([]);
      setBatchSponsorsToRemove([]);
      setBatchMode(false);
    }
  };

  // Select all visible images


  // Open batch re-analysis dialog
  const openBatchReanalysisDialog = () => {
    if (batchSelectedImages.length === 0) {
      alert('Please select at least one image to re-analyze');
      return;
    }
    if (batchSelectedImages.length > 10) {
      alert('Please select no more than 10 images for batch re-analysis');
      return;
    }
    setBatchReanalysisDialog(true);
  };

  // Process batch re-analysis
  const processBatchReanalysis = async () => {
    if (batchSelectedImages.length === 0) return;

    setReanalysisProcessing(true);
    setReanalysisProgress(0);
    setReanalysisTotal(batchSelectedImages.length);

    let successCount = 0;
    let failureCount = 0;

    for (let i = 0; i < batchSelectedImages.length; i++) {
      const imageId = batchSelectedImages[i];
      
      try {
        // Get the image details first
        const image = images.find(img => img.id === imageId);
        if (!image) {
          console.error(`Image ${imageId} not found`);
          failureCount++;
          continue;
        }

        // Check if image has failed status - skip these as they have no actual image data
        if (image.status === 'failed') {
          console.log(`Skipping failed image ${imageId} - no image data available for analysis`);
          failureCount++;
          continue;
        }

        // Re-analyze the image using the batch endpoint
        console.log(`Re-analyzing image ${i + 1}/${batchSelectedImages.length}: ${imageId}`);
        
        const response = await apiClient.post('/batch', {
          images: [{
            id: imageId,
            s3Key: image.s3Key,
            filename: image.filename || image.originalFilename || image.metadata?.filename || 'unknown',
            matchId: image.matchId // Preserve the match association
          }],
          options: {
            reanalysis: true, // Flag to indicate this is a re-analysis
            preserveMatchId: true // Ensure match association is preserved
          }
        });

        if (response.success) {
          successCount++;
          console.log(`Successfully queued re-analysis for image ${imageId}`);
        } else {
          failureCount++;
          console.error(`Failed to queue re-analysis for image ${imageId}:`, response.error);
        }
      } catch (error) {
        failureCount++;
        console.error(`Error re-analyzing image ${imageId}:`, error);
      }

      setReanalysisProgress(i + 1);
      
      // Small delay to prevent overwhelming the API
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Show completion message
    if (successCount > 0) {
      alert(`Re-analysis queued for ${successCount} image(s). ${failureCount > 0 ? `${failureCount} failed.` : ''} Please refresh the page in a few minutes to see updated results.`);
    } else {
      alert(`Failed to queue re-analysis for all ${failureCount} images. Please try again.`);
    }

    // Reset state
    setReanalysisProcessing(false);
    setBatchReanalysisDialog(false);
    setBatchSelectedImages([]);
    setBatchMode(false);
    
    // Refresh images after a short delay
    setTimeout(() => {
      fetchImages();
    }, 2000);
  };

  // Handle batch delete
  const handleBatchDelete = async () => {
    if (batchSelectedImages.length === 0) {
      alert('No images selected');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${batchSelectedImages.length} selected image${batchSelectedImages.length !== 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setBatchProcessing(true);
    setBatchProgress(0);
    setBatchTotal(batchSelectedImages.length);

    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < batchSelectedImages.length; i++) {
        const imageId = batchSelectedImages[i];
        try {
          await apiClient.delete(`${API_BASE_URL}/images/${imageId}`);
          successCount++;
        } catch (error) {
          console.error(`Error deleting image ${imageId}:`, error);
          errorCount++;
        }
        setBatchProgress(i + 1);
      }

      // Refresh the images data
      fetchImages();

      alert(`Batch delete complete. Successfully deleted ${successCount} image${successCount !== 1 ? 's' : ''}${errorCount > 0 ? `. ${errorCount} deletion${errorCount !== 1 ? 's' : ''} failed.` : '.'}`);
    } catch (error) {
      console.error('Error during batch delete:', error);
      alert('An unexpected error occurred during batch delete.');
    } finally {
      setBatchProcessing(false);
      setBatchSelectedImages([]);
      setBatchMode(false);
    }
  };

  // Handle remove failed uploads
  const handleRemoveFailedUploads = async () => {
    // Filter images with status === 'failed'
    const failedImages = images.filter(image => image.status === 'failed');
    
    if (failedImages.length === 0) {
      alert('No failed images found to remove');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${failedImages.length} failed image${failedImages.length !== 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setBatchProcessing(true);
    setBatchProgress(0);
    setBatchTotal(failedImages.length);

    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < failedImages.length; i++) {
        const imageId = failedImages[i].id;
        try {
          await apiClient.delete(`${API_BASE_URL}/images/${imageId}`);
          successCount++;
        } catch (error) {
          console.error(`Error deleting failed image ${imageId}:`, error);
          errorCount++;
        }
        setBatchProgress(i + 1);
      }

      // Refresh the images data
      fetchImages();

      alert(`Failed image cleanup complete. Successfully removed ${successCount} failed image${successCount !== 1 ? 's' : ''}${errorCount > 0 ? `. ${errorCount} deletion${errorCount !== 1 ? 's' : ''} failed.` : '.'}`);
    } catch (error) {
      console.error('Error during failed image cleanup:', error);
      alert('An unexpected error occurred during failed image cleanup.');
    } finally {
      setBatchProcessing(false);
    }
  };

  // Handle batch reassign match
  const handleBatchReassignMatch = () => {
    if (batchSelectedImages.length === 0) {
      alert('No images selected');
      return;
    }
    setBatchReassignDialog(true);
  };

  // Process batch reassign match
  const processBatchReassignMatch = async () => {
    if (!batchSelectedMatch) {
      alert('Please select a match');
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    setBatchTotal(batchSelectedImages.length);

    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < batchSelectedImages.length; i++) {
        const imageId = batchSelectedImages[i];
        try {
          await apiClient.put(`${API_BASE_URL}/images/${imageId}`, {
            matchId: batchSelectedMatch
          });
          successCount++;
        } catch (error) {
          console.error(`Error reassigning image ${imageId}:`, error);
          errorCount++;
        }
        setBatchProgress(i + 1);
      }

      // Refresh the images data
      fetchImages();

      alert(`Batch reassign complete. Successfully reassigned ${successCount} image${successCount !== 1 ? 's' : ''}${errorCount > 0 ? `. ${errorCount} reassignment${errorCount !== 1 ? 's' : ''} failed.` : '.'}`);
    } catch (error) {
      console.error('Error during batch reassign:', error);
      alert('An unexpected error occurred during batch reassign.');
    } finally {
      setBatchProcessing(false);
      setBatchReassignDialog(false);
      setBatchSelectedImages([]);
      setBatchSelectedMatch('');
      setBatchMode(false);
    }
  };

  // --- Render Functions ---

  const renderMatchesGrid = () => {
    // If selectedMatchId is present, it implies we came from another tab to view a specific match's images.
    // However, if the user then clears the match filter in LibraryTab, selectedMatch becomes empty.
    // The viewMode state correctly handles showing matches or images.
    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, index) => (
            <Card key={index}>
              <Skeleton className="h-48 w-full" />
              <CardContent className="p-2">
                <Skeleton className="h-4 w-3/4 mb-1" />
                <Skeleton className="h-3 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (matchesWithImages.length === 0) {
      return <p className="text-center text-gray-500 py-8">No matches with images found.</p>;
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {matchesWithImages.map(match => (
          <Card key={match.id} className="overflow-hidden cursor-pointer group" onClick={() => handleMatchClick(match.id)}>
            <img 
              src={match.imageUrl || '/placeholder-match.png'} 
              alt={match.title} 
              className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
              onError={(e) => { e.target.onerror = null; e.target.src='/placeholder-match.png'; }} 
            />
            <CardContent className="p-2">
              <h3 className="font-semibold truncate" title={match.title}>{match.title}</h3>
              <p className="text-sm text-gray-500">{match.imageCount} images</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderImagesGrid = () => {
    if (loading) {
      return <p>Loading...</p>;
    }

    if (images.length === 0) {
      return <p className="text-center p-4 text-muted-foreground">No images found for the current selection.</p>;
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-4">
        {displayedImages.map((image, index) => (
          <Card 
            key={image.id || index} 
            className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group relative bg-card"
            onClick={() => {
              if (batchMode) {
                // Batch mode selection is handled by the checkbox
              } else {
                handleOpenImageDetailModal(image);
              }
            }}
          >
            <CardContent className="p-0 aspect-video flex items-center justify-center bg-muted/30 relative">
              {batchMode && (
                <Checkbox
                  checked={batchSelectedImages.includes(image.id)}
                  onCheckedChange={() => toggleImageSelection(image.id)}
                  className="absolute top-2 left-2 z-10 bg-white data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  aria-label={`Select image ${image.metadata?.filename || image.originalFilename || image.filename || (image.s3Key ? image.s3Key.split('/').pop() : index + 1)}`}
                  onClick={(e) => e.stopPropagation()} // Prevent card click when clicking checkbox
                />
              )}
              {getImageUrl(image) ? (
                <img 
                  src={getImageUrl(image)} 
                  alt={image.metadata?.filename || image.originalFilename || image.filename || (image.s3Key ? image.s3Key.split('/').pop() : `Image ${index + 1}`)}
                  className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  decoding="async"
                  onError={async (e) => {
                    // Try to get a fresh URL if we haven't already retried
                    if (!e.target.dataset.retried) {
                      e.target.dataset.retried = 'true';
                      
                      const freshUrl = await getFreshImageUrl(image.id);
                      if (freshUrl && freshUrl !== e.target.src) {
                        e.target.src = freshUrl;
                        return; // Don't show placeholder yet, let the fresh URL load
                      }
                    }
                    
                    // Show placeholder on error
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (parent && !parent.querySelector('.error-placeholder')) {
                      const placeholder = document.createElement('div');
                      placeholder.className = 'error-placeholder w-full h-full flex items-center justify-center bg-gray-200 border-2 border-dashed border-gray-300';
                      placeholder.innerHTML = '<div class="text-gray-500 text-center"><div class="text-2xl">📷</div><div class="text-xs mt-1">Image unavailable</div></div>';
                      parent.appendChild(placeholder);
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <Eye className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-xs truncate font-semibold">
                {image.metadata?.filename || 
                 image.originalFilename || 
                 image.filename || 
                 (image.s3Key ? image.s3Key.split('/').pop() : 'Untitled Image')}
              </p>
                {/* Example: Display match title if available */}
                {/* {image.matchId && <p className="text-xs truncate">{getMatchTitle(image.matchId)}</p>} */}
              </div>
            </CardContent>
            {/* Optional: Footer for quick actions, but detail modal is primary interaction */}
            {/* <div className="p-2 border-t flex justify-between items-center"> <p className="text-xs text-muted-foreground">{image.sponsors?.length || 0} sponsors</p> </div> */}
          </Card>
        ))}
      </div>
    );
  };

  // ImageDetailModal is now a separate component, imported at the top.
  // The old inline definition is removed.

  // --- Render Functions for Dialogs (if not already defined or need adjustment) ---
  const renderTagDialog = () => {
    // Simplified version of existing tag dialog logic
    // Ensure this is consistent with how setShowTagDialog, selectedImage etc. are used
    if (!showTagDialog || !selectedImage) return null;
    return (
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tag Sponsors for: {selectedImage.metadata?.filename || selectedImage.originalFilename || selectedImage.filename || (selectedImage.s3Key ? selectedImage.s3Key.split('/').pop() : 'Image')}</DialogTitle>
            <DialogDescription>
              Select sponsors associated with this image.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[60vh] overflow-y-auto">
            {loadingSponsors && <p>Loading sponsors...</p>}
            {!loadingSponsors && availableSponsors.map(sponsor => (
              <div key={sponsor.id} className="flex items-center space-x-2 mb-2">
                <Checkbox 
                  id={`sponsor-${sponsor.id}`}
                  checked={selectedSponsorsForTagging.some(s => s.id === sponsor.id)}
                  onCheckedChange={() => toggleSponsor(sponsor)}
                />
                <label htmlFor={`sponsor-${sponsor.id}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {sponsor.name}
                </label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTagDialog(false)}>Cancel</Button>
            <Button onClick={saveSponsorsForImage}>Save Tags</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };



  // --- Main Return ---
  return (
    <div className="h-full flex flex-col bg-background text-foreground">
      {/* Header and Filters */}
      <div className="p-4 border-b bg-card">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h2 className="text-2xl font-semibold">
            {viewMode === 'matches' ? 'Match Library' : 
              selectedMatch ? `Images for: ${getMatchTitle(selectedMatch)}` : 'All Images'}
          </h2>
          <div className="flex items-center space-x-2">
            {viewMode === 'images' && selectedMatch && (
              <Button variant="outline" onClick={handleBackToMatches}>Back to Matches</Button>
            )}
            <Button 
              variant={batchMode ? "destructive" : "outline"} 
              onClick={toggleBatchMode}
              disabled={viewMode === 'matches'}
            >
              {batchMode ? 'Cancel Batch Mode' : 'Enable Batch Mode'}
            </Button>
          </div>
        </div>

        {viewMode === 'images' && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="unassigned-filter"
                  checked={showUnassignedOnly}
                  onCheckedChange={setShowUnassignedOnly}
                />
                <label htmlFor="unassigned-filter" className="text-sm font-medium cursor-pointer">
                  Show only unassigned images (no match)
                </label>
              </div>
            </div>
            
            {/* Sponsor and Player Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sponsor Filters */}
              <div>
                <h4 className="font-medium mb-2">Filter by Sponsors</h4>
                <div className="max-h-32 overflow-y-auto space-y-1 border rounded-md p-2">
                  {sponsors.map(sponsor => (
                    <div key={sponsor.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`sponsor-filter-${sponsor.id}`}
                        checked={selectedSponsors.includes(sponsor.id)}
                        onCheckedChange={() => handleSponsorChange(sponsor.id)}
                      />
                      <label htmlFor={`sponsor-filter-${sponsor.id}`} className="text-sm cursor-pointer">
                        {sponsor.name}
                      </label>
                    </div>
                  ))}
                  {sponsors.length === 0 && (
                    <p className="text-sm text-muted-foreground">No sponsors found</p>
                  )}
                </div>
              </div>
              
              {/* Player Filters */}
              <div>
                <h4 className="font-medium mb-2">Filter by Players</h4>
                <div className="max-h-32 overflow-y-auto space-y-1 border rounded-md p-2">
                  {sortedPlayers.map(player => (
                    <div key={player.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`player-filter-${player.id}`}
                        checked={selectedPlayers.includes(player.id)}
                        onCheckedChange={() => handlePlayerChange(player.id)}
                      />
                      <label htmlFor={`player-filter-${player.id}`} className="text-sm cursor-pointer">
                        {player.name}
                      </label>
                    </div>
                  ))}
                  {players.length === 0 && (
                    <p className="text-sm text-muted-foreground">No players found</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Batch Selection Controls */}
            {batchMode && (
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">
                    Batch Mode: {batchSelectedImages.length} of {displayedImages.length} images selected
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setBatchSelectedImages(displayedImages.map(img => img.id))}
                    disabled={batchSelectedImages.length === displayedImages.length}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setBatchSelectedImages([])}
                    disabled={batchSelectedImages.length === 0}
                  >
                    Select None
                  </Button>
                </div>
              </div>
            )}

            {/* Failed Images Cleanup */}
            {images.filter(img => img.status === 'failed').length > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-sm font-medium text-red-700">
                  {images.filter(img => img.status === 'failed').length} failed image{images.filter(img => img.status === 'failed').length !== 1 ? 's' : ''} found
                </span>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleRemoveFailedUploads}
                  disabled={batchProcessing}
                >
                  {batchProcessing ? `Removing... (${batchProgress}/${batchTotal})` : 'Remove Failed Images'}
                </Button>
              </div>
            )}

            {/* Batch Operations Bar */}
            {batchMode && batchSelectedImages.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-sm font-medium text-blue-700">
                  {batchSelectedImages.length} image{batchSelectedImages.length !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openBatchReanalysisDialog}
                    disabled={batchSelectedImages.length > 10}
                    title={batchSelectedImages.length > 10 ? "Select no more than 10 images for re-analysis" : "Re-analyze selected images with AI"}
                  >
                    Re-analyze ({batchSelectedImages.length}/10)
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleBatchReassignMatch}
                  >
                    Reassign Match
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openBatchRemoveSponsorsDialog}
                  >
                    Remove Sponsors
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={handleBatchDelete}
                    disabled={batchProcessing}
                  >
                    {batchProcessing ? `Deleting... (${batchProgress}/${batchTotal})` : 'Delete Selected'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-grow overflow-y-auto">
        {viewMode === 'matches' ? renderMatchesGrid() : renderImagesGrid()}
      </div>

      {/* Pagination controls */}
      {viewMode === 'images' && images.length > pageSize && (
        <div className="flex justify-center items-center space-x-2 p-4 border-t bg-card">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm">Page {currentPage} of {Math.ceil(images.length / pageSize)}</span>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(images.length / pageSize), prev + 1))} 
            disabled={currentPage === Math.ceil(images.length / pageSize)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Dialogs */}
      {showTagDialog && selectedImage && renderTagDialog()} 
      {/* Ensure renderTagDialog is defined and handles its state correctly */}
      
      {/* Image Detail Modal */}
      {isImageDetailModalOpen && selectedImageForDetail && (
        <Dialog open={isImageDetailModalOpen} onOpenChange={handleCloseImageDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-xl font-semibold">
                    {selectedImageForDetail.metadata?.filename || 
                     selectedImageForDetail.originalFilename || 
                     selectedImageForDetail.filename || 
                     (selectedImageForDetail.s3Key ? selectedImageForDetail.s3Key.split('/').pop() : 'Image Details')}
                  </DialogTitle>
                  <DialogDescription>
                    View detailed information about this image and its detected sponsors and players.
                    {displayedImages.length > 1 && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({displayedImages.findIndex(img => img.id === selectedImageForDetail.id) + 1} of {displayedImages.length})
                      </span>
                    )}
                  </DialogDescription>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Navigation buttons */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      const currentIndex = displayedImages.findIndex(img => img.id === selectedImageForDetail.id);
                      if (currentIndex > 0) {
                        setSelectedImageForDetail(displayedImages[currentIndex - 1]);
                      }
                    }}
                    disabled={displayedImages.findIndex(img => img.id === selectedImageForDetail.id) === 0}
                  >
                    ← Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      const currentIndex = displayedImages.findIndex(img => img.id === selectedImageForDetail.id);
                      if (currentIndex < displayedImages.length - 1) {
                        setSelectedImageForDetail(displayedImages[currentIndex + 1]);
                      }
                    }}
                    disabled={displayedImages.findIndex(img => img.id === selectedImageForDetail.id) === displayedImages.length - 1}
                  >
                    Next →
                  </Button>
                  
                  {!isEditingImageDetails ? (
                    <Button variant="outline" size="sm" onClick={handleStartEditingImageDetails}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={handleCancelEditingImageDetails}>
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveImageDetails}>
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </DialogHeader>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Image Display */}
              <div className="space-y-4">
                <div className="aspect-video bg-muted rounded-lg overflow-hidden relative">
                  {getImageUrl(selectedImageForDetail) ? (
                    <>
                      <img 
                        key={selectedImageForDetail.id}
                        src={getImageUrl(selectedImageForDetail)} 
                        alt={selectedImageForDetail.metadata?.filename || 'Image'}
                        className="w-full h-full object-contain"
                        onError={async (e) => {
                          if (!e.target.dataset.retried) {
                            e.target.dataset.retried = 'true';
                            const freshUrl = await getFreshImageUrl(selectedImageForDetail.id);
                            if (freshUrl) {
                              e.target.src = freshUrl;
                            } else {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }
                          } else {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="w-full h-full absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500" style={{ display: 'none' }}>
                        <div className="text-center">
                          <Eye className="w-12 h-12 mx-auto mb-2" />
                          <div className="text-sm">
                            {selectedImageForDetail.status === 'failed' ? 'Processing failed' : 'Failed to load image'}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <Eye className="w-12 h-12 mx-auto mb-2" />
                        <div className="text-sm">No image available</div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-2">
                  <Button 
                    onClick={async () => {
                      let imageUrl = getImageUrl(selectedImageForDetail);
                      
                      // If we have a URL, try to fetch it first to check if it's expired
                      if (imageUrl && selectedImageForDetail.id) {
                        try {
                          const response = await fetch(imageUrl, { method: 'HEAD' });
                          if (!response.ok) {
                            // URL might be expired, try to get a fresh one
                            const freshUrl = await getFreshImageUrl(selectedImageForDetail.id);
                            if (freshUrl) {
                              imageUrl = freshUrl;
                            }
                          }
                        } catch (error) {
                          // Fetch failed, try to get a fresh URL
                          const freshUrl = await getFreshImageUrl(selectedImageForDetail.id);
                          if (freshUrl) {
                            imageUrl = freshUrl;
                          }
                        }
                      }
                      
                      if (imageUrl) {
                        const link = document.createElement('a');
                        link.href = imageUrl;
                        link.download = selectedImageForDetail.metadata?.filename || 
                                       selectedImageForDetail.originalFilename || 
                                       selectedImageForDetail.filename || 
                                       (selectedImageForDetail.s3Key ? selectedImageForDetail.s3Key.split('/').pop() : 'image.jpg');
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                    className="w-full"
                    variant="outline"
                  >
                    Download Image
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      if (window.confirm('Re-analyze this image with AI? This will update the detected sponsors and players.')) {
                        handleSingleImageReanalysis(selectedImageForDetail);
                      }
                    }}
                    className="w-full"
                    variant="outline"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Re-analyze with AI'}
                  </Button>
                </div>
              </div>
              
              {/* Image Details */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Image Information</h3>
                  {!isEditingImageDetails ? (
                    <div className="space-y-2">
                      <div>
                        <span className="font-medium">Filename:</span>
                        <span className="ml-2 text-muted-foreground">
                          {selectedImageForDetail.metadata?.filename || 
                           selectedImageForDetail.originalFilename || 
                           selectedImageForDetail.filename || 
                           (selectedImageForDetail.s3Key ? selectedImageForDetail.s3Key.split('/').pop() : 'Unknown')}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Uploader:</span>
                        <span className="ml-2 text-muted-foreground">
                          {selectedImageForDetail.metadata?.uploader || selectedImageForDetail.uploader || 'System'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Uploaded:</span>
                        <span className="ml-2 text-muted-foreground">
                          {selectedImageForDetail.timestamp ? new Date(selectedImageForDetail.timestamp).toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Match:</span>
                        <span className="ml-2 text-muted-foreground">
                          {selectedImageForDetail.matchId ? getMatchTitle(selectedImageForDetail.matchId) : 'No match assigned'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Filename:</label>
                        <Input
                          value={editingImageData.filename}
                          onChange={(e) => setEditingImageData(prev => ({ ...prev, filename: e.target.value }))}
                          placeholder="Enter filename"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Match:</label>
                        <Select 
                          value={editingImageData.matchId || 'no-match'} 
                          onValueChange={(value) => setEditingImageData(prev => ({ ...prev, matchId: value === 'no-match' ? '' : value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a match" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no-match">No match assigned</SelectItem>
                            {matches.map(match => (
                              <SelectItem key={match.id} value={match.id}>
                                {getMatchTitle(match.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Uploader:</label>
                        <Input
                          value={editingImageData.uploader}
                          onChange={(e) => setEditingImageData(prev => ({ ...prev, uploader: e.target.value }))}
                          placeholder="Enter uploader name"
                        />
                      </div>
                      <div>
                        <span className="font-medium">Uploaded:</span>
                        <span className="ml-2 text-muted-foreground">
                          {selectedImageForDetail.timestamp ? new Date(selectedImageForDetail.timestamp).toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  )}
                    <div>
                      <span className="font-medium">Analysis Status:</span>
                      <span className="ml-2">
                        {selectedImageForDetail.status === 'completed' ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">Analyzed</Badge>
                        ) : selectedImageForDetail.status === 'pending' ? (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Processing</Badge>
                        ) : selectedImageForDetail.status === 'failed' ? (
                          <Badge variant="destructive">Failed</Badge>
                        ) : (
                          <Badge variant="outline">Unknown</Badge>
                        )}
                      </span>
                    </div>
                    {selectedImageForDetail.metadata?.fileSize && (
                      <div>
                        <span className="font-medium">File Size:</span>
                        <span className="ml-2 text-muted-foreground">
                          {(selectedImageForDetail.metadata.fileSize / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    )}
                    {selectedImageForDetail.processingError && (
                      <div>
                        <span className="font-medium text-red-600">Error:</span>
                        <span className="ml-2 text-red-600 text-sm">
                          {selectedImageForDetail.processingError}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Detected Sponsors */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Detected Sponsors</h3>
                    {!isEditingSponsors ? (
                      <Button variant="outline" size="sm" onClick={handleEditSponsors}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={handleCancelEditSponsors}>
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveSponsors}>
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {!isEditingSponsors ? (
                    selectedImageForDetail.sponsors && selectedImageForDetail.sponsors.length > 0 ? (
                      <div className="space-y-2">
                        {selectedImageForDetail.sponsors.map((sponsorId, index) => {
                          const sponsor = sponsors.find(s => s.id === sponsorId);
                          return (
                            <div key={sponsorId || index} className="flex items-center justify-between p-2 bg-muted rounded">
                              <span>{sponsor?.name || sponsorId}</span>
                              <Badge variant="secondary">Detected</Badge>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No sponsors detected</p>
                    )
                  ) : (
                    <div className="space-y-4">
                      {/* Current sponsors being edited */}
                      <div className="space-y-2">
                        {editingSponsors.map((sponsorId, index) => {
                          const sponsor = sponsors.find(s => s.id === sponsorId);
                          return (
                            <div key={sponsorId || index} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                              <span>{sponsor?.name || sponsorId}</span>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleRemoveSponsor(sponsorId)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Add sponsor dropdown */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Add Sponsor:</label>
                        <Select onValueChange={handleAddSponsor}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a sponsor to add" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px] overflow-y-auto">
                            {sponsors
                              .filter(sponsor => !editingSponsors.includes(sponsor.id))
                              .map(sponsor => (
                                <SelectItem key={sponsor.id} value={sponsor.id}>
                                  {sponsor.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Detected Players */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Detected Players</h3>
                    {!isEditingPlayers ? (
                      <Button variant="outline" size="sm" onClick={handleEditPlayers}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={handleCancelEditPlayers}>
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSavePlayers}>
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {!isEditingPlayers ? (
                    selectedImageForDetail.players && selectedImageForDetail.players.length > 0 ? (
                      <div className="space-y-2">
                        {selectedImageForDetail.players.map((player, index) => (
                          <div key={index} className="p-3 bg-green-50 border border-green-200 rounded">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{player.name}</span>
                              <Badge variant="outline" className="text-green-700 border-green-300">
                                #{player.jerseyNumber}
                              </Badge>
                            </div>
                            <div className="text-sm text-green-600 space-y-1">
                              <div><strong>Position:</strong> {player.position}</div>
                              <div><strong>Team:</strong> {player.team}</div>
                              <div><strong>Action:</strong> {player.action}</div>
                              {player.details && <div><strong>Details:</strong> {player.details}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No players detected</p>
                    )
                  ) : (
                    <div className="space-y-4">
                      {/* Current players being edited */}
                      <div className="space-y-2">
                        {sortedEditingPlayers.map((player, index) => (
                          <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{player.name}</span>
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline" className="text-blue-700 border-blue-300">
                                  #{player.jerseyNumber}
                                </Badge>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleRemovePlayer(index)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="text-sm text-blue-600 space-y-1">
                              <div><strong>Position:</strong> {player.position}</div>
                              <div><strong>Team:</strong> {player.team}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Add player dropdown */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Add Player:</label>
                        <Select onValueChange={handleAddPlayer}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a player to add" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px] overflow-y-auto">
                            {sortedPlayers
                              .filter(player => !editingPlayers.find(p => p.name === player.name))
                              .map(player => (
                                <SelectItem key={player.id} value={player.id}>
                                  #{player.jerseyNumber} {player.name} ({player.position})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Analysis Results - Show if analysis exists OR if sponsors/players detected */}
                {(selectedImageForDetail.analysis || 
                  (selectedImageForDetail.sponsors && selectedImageForDetail.sponsors.length > 0) ||
                  (selectedImageForDetail.players && selectedImageForDetail.players.length > 0)) && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold">AI Analysis</h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowAiResponse(!showAiResponse)}
                      >
                        {showAiResponse ? 'Hide' : 'Show'} AI Response
                      </Button>
                    </div>
                    {showAiResponse && (
                      <div className="p-3 bg-muted rounded text-sm">
                        {selectedImageForDetail.analysis ? (
                          <pre className="whitespace-pre-wrap">
                            {typeof selectedImageForDetail.analysis === 'string' 
                              ? selectedImageForDetail.analysis 
                              : selectedImageForDetail.analysis?.raw || JSON.stringify(selectedImageForDetail.analysis, null, 2)}
                          </pre>
                        ) : (
                          <div>
                            <p className="text-gray-600 mb-2">Raw analysis not available, but processed results:</p>
                            <pre className="whitespace-pre-wrap text-xs">
{`Sponsors: ${selectedImageForDetail.sponsors ? JSON.stringify(selectedImageForDetail.sponsors, null, 2) : 'None'}

Players: ${selectedImageForDetail.players ? JSON.stringify(selectedImageForDetail.players, null, 2) : 'None'}

Status: ${selectedImageForDetail.status || 'Unknown'}`}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseImageDetailModal}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Batch Reassign Match Dialog */}
      {batchReassignDialog && (
        <Dialog open={batchReassignDialog} onOpenChange={setBatchReassignDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reassign Match</DialogTitle>
              <DialogDescription>
                Reassign {batchSelectedImages.length} selected image{batchSelectedImages.length !== 1 ? 's' : ''} to a different match.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <label className="block text-sm font-medium mb-2">Select Match:</label>
              <select 
                value={batchSelectedMatch} 
                onChange={(e) => setBatchSelectedMatch(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a match...</option>
                {matches && matches.length > 0 ? (
                  matches.map(match => (
                    <option key={match.id} value={match.id}>
                      {getMatchTitle(match.id)}
                    </option>
                  ))
                ) : (
                  <option disabled>No matches available</option>
                )}
              </select>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBatchReassignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={processBatchReassignMatch} disabled={!batchSelectedMatch}>
                Reassign Images
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Batch Remove Sponsors Dialog */}
      {batchRemoveSponsorsDialog && (
        <Dialog open={batchRemoveSponsorsDialog} onOpenChange={setBatchRemoveSponsorsDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Remove Sponsors</DialogTitle>
              <DialogDescription>
                Remove selected sponsors from {batchSelectedImages.length} selected image{batchSelectedImages.length !== 1 ? 's' : ''}.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <label className="block text-sm font-medium mb-2">Select Sponsors to Remove:</label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-2">
                {availableSponsors.length > 0 ? (
                  availableSponsors.map(sponsor => (
                    <div key={sponsor.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`remove-sponsor-${sponsor.id}`}
                        checked={batchSponsorsToRemove.includes(sponsor.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setBatchSponsorsToRemove(prev => [...prev, sponsor.id]);
                          } else {
                            setBatchSponsorsToRemove(prev => prev.filter(id => id !== sponsor.id));
                          }
                        }}
                      />
                      <label htmlFor={`remove-sponsor-${sponsor.id}`} className="text-sm cursor-pointer">
                        {sponsor.name}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Loading sponsors...</p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBatchRemoveSponsorsDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={processBatchRemoveSponsors} 
                disabled={batchSponsorsToRemove.length === 0 || batchProcessing}
                variant="destructive"
              >
                {batchProcessing ? 'Removing...' : `Remove from ${batchSelectedImages.length} Images`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Batch Re-analysis Dialog */}
      {batchReanalysisDialog && (
        <Dialog open={batchReanalysisDialog} onOpenChange={setBatchReanalysisDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Re-analyze Images</DialogTitle>
              <DialogDescription>
                Re-analyze {batchSelectedImages.length} selected image{batchSelectedImages.length !== 1 ? 's' : ''} with AI to detect sponsors and players.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="w-4 h-4 bg-amber-400 rounded-full mt-0.5 flex-shrink-0"></div>
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Important Notes:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• This will re-process the selected images through AI analysis</li>
                      <li>• Processing may take a few minutes to complete</li>
                      <li>• You can navigate away and return later - processing continues in background</li>
                      <li>• Existing sponsor and player detections will be updated</li>
                      <li>• Images will remain associated with their current matches</li>
                      <li>• Maximum 10 images per batch to prevent timeouts</li>
                    </ul>
                  </div>
                </div>
              </div>

              {reanalysisProcessing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing images...</span>
                    <span>{reanalysisProgress}/{reanalysisTotal}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${(reanalysisProgress / reanalysisTotal) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setBatchReanalysisDialog(false)}
                disabled={reanalysisProcessing}
              >
                Cancel
              </Button>
              <Button 
                onClick={processBatchReanalysis}
                disabled={reanalysisProcessing}
              >
                {reanalysisProcessing ? 'Processing...' : 'Start Re-analysis'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* {batchTagDialog && renderBatchTagDialog()} */}
      {/* Ensure renderBatchTagDialog is defined and handles its state correctly */}

    </div>
  );
}